import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  ViewChildren,
  inject,
  input,
} from '@angular/core';
import { CustomerSupportBusinessFlowSettings } from '@app/admin/components/business-flows/business-flow.types';
import { PlaybookDetectionCategories } from '@app/admin/components/smartclaim/smartclaim-types';
import config from '@config';
import { SHARED_MASTER_DATA_PAYOR_MASTER_LOOKUP_TYPES } from '@libs/shared/domain/master-data/payor-master-lookup/shared-master-data-payor-master-lookup.type';
import { SharedBusinessFlowProcessFlowBVerificationSettingsSchema } from '@libs/shared/domain/patient-assistance/business-flow/process-flow/b-verification/schema/shared-business-flow-process-flow-pharmacy-b-verification-settings.schema';
import { SHARED_BUSINESS_FLOW_PROCESS_FLOW_B_VERIFICATION_TYPE } from '@libs/shared/domain/patient-assistance/business-flow/process-flow/b-verification/shared-business-flow-process-flow-b-verification.type';
import { SHARED_BUSINESS_FLOW_PROCESS_FLOW_CUSTOMER_SUPPORT_PROCESS_STATE_NAME_MAP } from '@libs/shared/domain/patient-assistance/business-flow/process-flow/customer-support/shared-business-flow-process-flow-customer-support-process-state-name.map';
import {
  ARCHIVED_STATE,
  COMPLETED_STATE,
  WAITING_FOR_PATIENT_STATE,
  WAITING_FOR_PHARMACY_STATE,
} from '@libs/shared/domain/patient-assistance/business-flow/process-flow/customer-support/shared-business-flow-process-flow-customer-support-process-state.type';
import { SharedBusinessFlowProcessFlowPharmacyPatientEngagementPharmacyPatientProductSettingsSchema } from '@libs/shared/domain/patient-assistance/business-flow/process-flow/pharmacy-patient-engagement/schema/shared-business-flow-process-flow-pharmacy-patient-engagement-pharmacy-patient-product-settings.schema';
import { SHARED_PATIENT_ASSISTANCE_OCC_LIST } from '@libs/shared/domain/patient-assistance/occ/shared-patient-assistance-occ.type';
import { SharedPatientAssistancePharmacyEnrollmentGroupVo } from '@libs/shared/domain/patient-assistance/pharmacy/enrollment-group/shared-patient-assistance-pharmacy-enrollment-group.vo';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { ClaimFlagsService } from '@services/claim-flags.service';
import { DataDeliveryService } from '@services/data-delivery.service';
import { DialogService } from '@services/dialog.service';
import { LookupsService } from '@services/lookups.service';
import { PharmacyFamily } from '@services/models/pharmacy-family.model';
import { TerminalWorkflowSettings } from '@services/models/terminal';
import { PharmacyChainService } from '@services/pharmacy-chains/pharmacy-chain.service';
import { PharmacyPaymentStatementsService } from '@services/pharmacy-payment-statements.service';
import { PharmacyVendorsService } from '@services/pharmacy-vendors.service';
import { PharmacyService } from '@services/pharmacy.service';
import { PlaybookService } from '@services/playbook.service';
import { ProductsService } from '@services/products.service';
import { ProductsStore } from '@services/products.store';
import { NETWORK_GROUP_LABEL_MAP, ProgramGroupService } from '@services/program-group.service';
import { SettingsService } from '@services/settings.service';
import { SettingsStore } from '@services/settings.store';
import { StatesService } from '@services/states.service';
import { TenantService } from '@services/tenant.service';
import { UserService } from '@services/user.service';
import { BaseComponent } from '@shared/components/base-component';
import { Column, Filter } from '@shared/components/data-view/data-view-types';
import { DataViewFilterGalleryComponent } from '@shared/components/data-view/filter-gallery/data-view-filter-gallery.component';
import { updateFilterWithDisplayValue } from '@shared/components/data-view/filter-helpers';
import { DataViewFilterOptionsService } from '@shared/dataview/options/filter-options.registry';
import { NgChanges } from '@shared/ng-changes';
import { chain, keyBy, remove, sortBy, uniq } from 'lodash';
import { firstValueFrom, lastValueFrom, map } from 'rxjs';

@Component({
  selector: 'app-data-view-filters',
  templateUrl: './data-view-filters.component.html',
  styleUrls: ['./data-view-filters.component.scss'],
  standalone: false,
})
export class DataViewFiltersComponent extends BaseComponent implements OnChanges {
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() appliedFilters: Filter.AppliedDataViewFilter[] = [];
  @Input() pageName: string;
  @Input() isLoadingOptions = false;
  @Input() viewMode: 'chips' | 'rows' = 'chips';
  @Output() isLoadingOptionsChange = new EventEmitter<boolean>();
  @Output() appliedFiltersChange = new EventEmitter<Filter.AppliedDataViewFilter[]>();
  @ViewChildren('filterPopover') filterPopovers: QueryList<NgbPopover>;
  activeFilter?: Filter.AppliedDataViewFilter;
  isOffcanvasOpen = false;
  filterOptionsLoaded = false;
  showClearAll = false;

  claimFlagFilterSubcategories = input<string[]>();
  availableFilters = input<Filter.DataViewFilter[]>();
  filterCategories = input<Column.DataViewColumnGalleryCategory[]>();

  private readonly settingsService = inject(SettingsService);
  private readonly playbookService = inject(PlaybookService);
  private readonly settingsStore = inject(SettingsStore);
  private readonly programGroupService = inject(ProgramGroupService);
  private readonly pharmacyService = inject(PharmacyService);
  private readonly pharmacyChainService = inject(PharmacyChainService);
  private dialogService = inject(DialogService);
  private productsService = inject(ProductsService);
  private productsStore = inject(ProductsStore);
  private tenantService = inject(TenantService);
  private pharmacyVendorService = inject(PharmacyVendorsService);
  private claimFlagsService = inject(ClaimFlagsService);
  private dataDeliveryService = inject(DataDeliveryService);
  private lookupsService = inject(LookupsService);
  private userService = inject(UserService);
  private filterOptionsService = inject(DataViewFilterOptionsService);

  @Output()
  filterChanged = new EventEmitter<Filter.AppliedDataViewFilter[]>();
  @Output()
  filtersCleared = new EventEmitter<void>();

  private paymentStatementsService = inject(PharmacyPaymentStatementsService);

  ngOnChanges(changes: NgChanges<this>): void {
    if (changes.appliedFilters) {
      this.setFilters(this.appliedFilters);
    }
  }

  async setFilters(appliedFilters: Filter.AppliedDataViewFilter[]) {
    this.isLoadingOptionsChange.next(true);
    for (const filter of appliedFilters) {
      this.updateFilterIcons(filter);
      if ((filter.options ?? []).length === 0) {
        await this.getFilterOptions(filter);
      }
    }

    this.appliedFilters = appliedFilters;
    this.showClearAll = appliedFilters.filter(filter => !filter.required).length > 0;
    this.filterOptionsLoaded = true;
    this.isLoadingOptionsChange.next(false);
    this.cdr.markForCheck();
  }

  forcePopoversClosed() {
    for (const popover of this.filterPopovers) {
      popover.close();
    }
  }

  addFilter() {
    for (const popover of this.filterPopovers) {
      popover.close();
    }
    if (this.isOffcanvasOpen) return;
    const offcanvas = this.dialogService.openSlideout(DataViewFilterGalleryComponent, {
      position: 'end',
    });
    this.isOffcanvasOpen = true;
    const offcanvasInstance = offcanvas.componentInstance;
    offcanvasInstance.appliedFilters = this.appliedFilters;
    const availableFilters = this.availableFilters();
    if (availableFilters) {
      offcanvasInstance.setFilters(
        availableFilters.map(filter => ({ ...filter })),
        this.filterCategories() ?? [],
      );
    } else {
      offcanvasInstance.pageName = this.pageName;
    }
    this.subscribe(offcanvas.hidden, async () => {
      this.isOffcanvasOpen = false;
      const newFilters = offcanvasInstance.newlyAppliedFilters;
      const removeFilterIds = offcanvasInstance.newlyRemovedFilterIds;
      if ((!newFilters || newFilters.length === 0) && (!removeFilterIds || removeFilterIds.length === 0)) {
        return;
      }

      const removedFilters = remove(this.appliedFilters, appliedFilter => removeFilterIds.includes(appliedFilter.id));
      if (removedFilters.length > 0) {
        this.filterChanged.next(
          removedFilters.map(removedFilter => ({
            ...removedFilter,
            value: undefined,
          })),
        );
      }
      await this.setFilters([...this.appliedFilters, ...newFilters]);
      this.filterChanged.next(newFilters);
      this.appliedFiltersChange.next(this.appliedFilters);
      setTimeout(() => {
        if (newFilters && newFilters.length > 0) {
          this.openPopover(newFilters[newFilters.length - 1], this.filterPopovers.last);
          this.cdr.markForCheck();
        }
      }, 250); // Wait for the adding animation to finish
    });
  }

  filterEdited(newFilter: Filter.AppliedDataViewFilter) {
    if (!newFilter) return;
    const oldFilterIndex = this.appliedFilters.findIndex(f => f.field === newFilter.field);
    if (oldFilterIndex !== -1) {
      this.updateFilterIcons(newFilter);
      this.appliedFilters[oldFilterIndex].type = newFilter.type;
      this.appliedFilters[oldFilterIndex].value = newFilter.value;
      this.appliedFilters[oldFilterIndex].timezone = newFilter.timezone;
      this.appliedFilters[oldFilterIndex].icon = newFilter.icon;
      this.appliedFilters[oldFilterIndex].iconText = newFilter.iconText;
      this.appliedFilters[oldFilterIndex].iconTextPostfix = newFilter.iconTextPostfix;
      this.appliedFilters[oldFilterIndex].displayValue = newFilter.displayValue;
    }
    this.filterChanged.next([newFilter]);
    this.appliedFiltersChange.next(this.appliedFilters);
  }

  openPopover(filter: Filter.AppliedDataViewFilter, popover?: NgbPopover) {
    for (const popover of this.filterPopovers) {
      popover.close();
    }
    this.activeFilter = filter;
    if (popover) {
      popover.open();
    }
  }

  removeFilter(filterIndex: number) {
    const filterToRemove = this.appliedFilters[filterIndex];
    if (filterToRemove.required) {
      return;
    }
    this.appliedFilters = this.appliedFilters.filter((f, index) => index !== filterIndex);
    this.filterChanged.next([
      {
        ...filterToRemove,
        value: undefined,
      },
    ]);
    this.appliedFiltersChange.next(this.appliedFilters);
  }

  clearAllClicked() {
    this.appliedFilters = [];
    this.filtersCleared.next();
    if (this.viewMode === 'rows') {
      this.appliedFiltersChange.next(this.appliedFilters);
    }
  }

  updateFilterIcons(filter: Filter.AppliedDataViewFilter) {
    if (filter.value !== undefined && filter.type) {
      updateFilterWithDisplayValue(filter);
    } else {
      filter.icon = undefined;
      filter.iconText = undefined;
      filter.iconTextPostfix = undefined;
      filter.displayValue = undefined;
    }
  }

  private async getFilterOptions(filter: Filter.AppliedDataViewFilter) {
    const userAccessibleTenants = this.userService.getUserAccessibleTenants();
    const groupOptionsByTenant = userAccessibleTenants.length > 1;
    const tenantsMap = keyBy(userAccessibleTenants, 'id');

    if (filter.optionsSource) {
      filter.options = await firstValueFrom(this.filterOptionsService.options(filter));
    } else if (filter.id === 'customerSupportBusinessFlowProcessFlowState') {
      const allowedStates = [ARCHIVED_STATE, COMPLETED_STATE, WAITING_FOR_PATIENT_STATE, WAITING_FOR_PHARMACY_STATE];
      filter.options = Object.entries(SHARED_BUSINESS_FLOW_PROCESS_FLOW_CUSTOMER_SUPPORT_PROCESS_STATE_NAME_MAP)
        .filter(([key]) => allowedStates.includes(key))
        .map(([key, value]) => ({ value: key, name: value }));
    } else if (filter.id.endsWith('State')) {
      filter.options = StatesService.getStates().map(state => ({
        name: state.name ?? state.value,
        value: state.value,
      }));
    } else if (filter.id === 'occ') {
      filter.options = SHARED_PATIENT_ASSISTANCE_OCC_LIST.map(item => ({ name: item, value: item }));
    } else if (filter.id === 'primaryPayorPlanType') {
      filter.options = chain(SHARED_MASTER_DATA_PAYOR_MASTER_LOOKUP_TYPES)
        .sortBy(item => item.toLowerCase())
        .map(item => ({ name: item, value: item }))
        .value();
    } else if (
      filter.id === 'productNdc' ||
      filter.id === 'customerSupportBusinessFlowProductNdc' ||
      filter.id === 'bVerificationBusinessFlowProductNdc'
    ) {
      filter.options = [];

      let allowedNdcs: string[] | undefined;

      if (filter.id === 'customerSupportBusinessFlowProductNdc') {
        const customerSupportSettings = await lastValueFrom(
          this.settingsStore
            .getSettings<CustomerSupportBusinessFlowSettings>('businessFlow', 'CustomerSupport')
            .pipe(map(settings => settings)),
        );

        if (customerSupportSettings) {
          allowedNdcs = uniq(
            [
              ...Object.values(customerSupportSettings.pharmacyCaseTypeDropdowns.programGroup),
              ...Object.values(customerSupportSettings.patientCaseTypeDropdowns.programGroup),
            ].flatMap(item => item.productNdcs ?? []),
          );
        }
      }

      const productsMap = await lastValueFrom(this.productsStore.getAll());
      for (const [tenantId, tenantProducts] of Object.entries(productsMap)) {
        let filteredTenantProducts = [...tenantProducts];
        if (allowedNdcs) {
          filteredTenantProducts = filteredTenantProducts.filter(product => allowedNdcs.includes(product.ndc));
        }

        if (filteredTenantProducts.length === 0) continue;

        const products = this.productsService.mapSelectList(filteredTenantProducts);
        const tenantName = tenantsMap[tenantId]?.name ?? tenantId;
        if (groupOptionsByTenant) products.forEach(product => (product.group = tenantName));
        filter.options.push(...products);
      }
    } else if (filter.id === 'tenantId' || filter.id === 'customerSupportBusinessFlowTenantId') {
      filter.options = userAccessibleTenants.map(tenant => ({
        name: tenant.name,
        value: tenant.id,
      }));

      if (filter.id === 'customerSupportBusinessFlowTenantId') {
        filter.options.push({
          name: 'Unassigned',
          value: 'unassigned',
        });
      }
    } else if (filter.id === 'claimStatus') {
      filter.options = [];
      const claimResultTypes = config.claimResultTypes;
      filter.options.push(
        ...claimResultTypes.map(item => ({
          name: item.toString(),
          value: item,
        })),
      );
    } else if (filter.id === 'pharmacyFamily') {
      filter.options = [];
      const familyOptions = await lastValueFrom(this.lookupsService.getSettings<PharmacyFamily[]>('pharmacy', 'family'));
      if (familyOptions) {
        filter.options = familyOptions.map(familyOption => ({
          name: familyOption.name,
          value: familyOption.id,
        }));
      }
    } else if (filter.id === 'destination') {
      filter.options = this.userService.isSuperAdmin()
        ? await lastValueFrom(this.dataDeliveryService.getDestinationsSelectLisItems(true)).catch(() => [])
        : [];
    } else if (filter.id === 'pharmacyChain') {
      filter.options = [];
      const chainOptions = await lastValueFrom(this.pharmacyChainService.search({}, { pageSize: 1000 }));
      if (chainOptions.items) {
        filter.options = chainOptions.items.map(chainOption => ({
          name: chainOption.name ?? `UNKNOWN (${chainOption.npi})`,
          value: chainOption.npi,
        }));
      }
    } else if (filter.id === 'pharmacyProgramGroup' || filter.id === 'programGroupId') {
      const programGroups: Record<string, SharedPatientAssistancePharmacyEnrollmentGroupVo[]> =
        this.programGroupService.filterAndSortBlockedProgramGroupsForDataview(
          await lastValueFrom(this.programGroupService.getAllProgramGroupsForCurrentUser()),
          filter.id,
          UserService.getCurrentRole(),
        );

      filter.options = [];
      for (const [tenantId, tenantProgramGroups] of Object.entries(programGroups).sort(([keyA], [keyB]) => keyA.localeCompare(keyB))) {
        let lastNetworkType: string = '';
        let subGroupTitle: string | undefined = undefined;
        for (const programGroup of tenantProgramGroups) {
          if (groupOptionsByTenant) subGroupTitle = undefined;
          if (lastNetworkType !== programGroup.networkType) {
            subGroupTitle = NETWORK_GROUP_LABEL_MAP[programGroup.networkType];
            lastNetworkType = programGroup.networkType;
          }
          filter.options.push({
            name: programGroup.name,
            value: `groupBy:${tenantId}|${programGroup.id}`,
            group: groupOptionsByTenant ? (this.tenantService.getTenantById(tenantId)?.name ?? 'Other') : undefined,
            subGroupTitle,
          });
        }
      }
    } else if (filter.id === 'pharmacypbaEnrollmentSystemVendor') {
      filter.options = [];
      const options = await lastValueFrom(this.pharmacyVendorService.getPharmacySystemVendors());
      filter.options = sortBy(options, 'systemVendor').map(option => ({
        name: option.name ? option.name : option.id,
        value: option.id,
      }));
    } else if (filter.id == 'claimFlagsList') {
      const allTenantsFlags = await lastValueFrom(this.claimFlagsService.getAllFlagsSettings());
      const flags = chain(Object.values(allTenantsFlags)).flatten().uniqBy('name').value();
      const isUserSuperAdmin = this.userService.isSuperAdmin();
      filter.options = flags
        .filter(flag => (isUserSuperAdmin ? true : !['Adjudication', undefined].includes(flag.category)))
        .map(flag => ({
          value: flag.name,
          name: this.claimFlagsService.interpolateFlagSettingTitle(flag),
          group: flag.category ?? 'Uncategorized',
          badge: flag.is_for_patient ? 'Patient Level' : undefined,
          badgeClass: flag.is_for_patient ? 'bg-info' : undefined,
        }));
    } else if (filter.id === 'pharmacyPaymentStatementStatus') {
      filter.options = this.paymentStatementsService.createAggregatedStatusDropdownItems();
    } else if (filter.id === 'claimPlaybookFlagsSubcategories') {
      filter.options = this.claimFlagFilterSubcategories()?.map(subcategory => ({ name: subcategory, value: subcategory })) ?? [];
    } else if (filter.id === 'brightscripTerminalWorkflows') {
      filter.options = [];
      const availableWorkflows = await lastValueFrom(
        this.settingsService.getList<Pick<TerminalWorkflowSettings, 'name' | 'showAsFilterOption' | 'tenants'>>(
          'brightscripTERMINAL_workflow',
        ),
      );
      filter.options = availableWorkflows
        .filter(workflow => workflow.showAsFilterOption && workflow.tenants?.some(tenant => tenantsMap[tenant]))
        .map(workflow => ({ name: workflow.name, value: workflow.attribute }));
    } else if (filter.id === 'suspiciousActivity') {
      filter.options = [];
      const playbooks = await lastValueFrom(this.playbookService.getPlaybooks());
      const detectionCategories = await lastValueFrom(
        this.settingsStore.getSettings<PlaybookDetectionCategories>('flags', 'detection-categories').pipe(map(settings => settings ?? {})),
      );
      for (const playbook of playbooks) {
        const detectionsMap = detectionCategories[playbook.id] ?? {};
        for (const detection of Object.values(detectionsMap)) {
          filter.options.push({ name: detection.title, value: detection.flagRules.join('-'), group: playbook.title });
        }
      }
    } else if (
      [
        'customerSupportBusinessFlowProgramGroup',
        'customerSupportBusinessFlowCallReason',
        'customerSupportBusinessFlowStakeholderType',
        'customerSupportBusinessFlowPharmacyType',
        'customerSupportBusinessFlowReportedBy',
        'operator',
        'customerSupportBusinessFlowAssignedTo',
        'operatorPhoneNumber',
      ].includes(filter.id)
    ) {
      filter.options = [];
      const customerSupportSettings = await lastValueFrom(
        this.settingsStore
          .getSettings<CustomerSupportBusinessFlowSettings>('businessFlow', 'CustomerSupport')
          .pipe(map(settings => settings)),
      );

      if (!customerSupportSettings) {
        return filter;
      }

      const addGroupedDropdownOptions = (
        filter: Filter.AppliedDataViewFilter,
        dropdown: Record<string, { name: string }>,
        caseType: 'Patient' | 'Pharmacy',
      ): void => {
        for (const [key, value] of Object.entries(dropdown)) {
          filter.options?.push({
            value: `groupBy:${caseType}|${key}`,
            name: value.name,
            group: `${caseType} Case`,
          });
        }
      };

      const addSimpleDropdownOptions = (filter: Filter.AppliedDataViewFilter, dropdown: Record<string, { name: string }>): void => {
        for (const [key, value] of Object.entries(dropdown)) {
          filter.options?.push({ value: key, name: value.name });
        }
      };

      const filterHandlers: Record<string, () => void> = {
        customerSupportBusinessFlowStakeholderType: () => {
          addGroupedDropdownOptions(filter, customerSupportSettings.patientCaseTypeDropdowns.stakeholderType, 'Patient');
        },
        customerSupportBusinessFlowPharmacyType: () => {
          addGroupedDropdownOptions(filter, customerSupportSettings.patientCaseTypeDropdowns.pharmacyType, 'Patient');
        },
        customerSupportBusinessFlowCallReason: () => {
          addGroupedDropdownOptions(filter, customerSupportSettings.pharmacyCaseTypeDropdowns.callReason, 'Pharmacy');
          addGroupedDropdownOptions(filter, customerSupportSettings.patientCaseTypeDropdowns.callReason, 'Patient');
        },
        customerSupportBusinessFlowProgramGroup: () => {
          addGroupedDropdownOptions(filter, customerSupportSettings.pharmacyCaseTypeDropdowns.programGroup, 'Pharmacy');
          addGroupedDropdownOptions(filter, customerSupportSettings.patientCaseTypeDropdowns.programGroup, 'Patient');
        },
        customerSupportBusinessFlowReportedBy: () => {
          addGroupedDropdownOptions(filter, customerSupportSettings.pharmacyCaseTypeDropdowns.reportedBy, 'Pharmacy');
          addGroupedDropdownOptions(filter, customerSupportSettings.patientCaseTypeDropdowns.reportedBy, 'Patient');
        },
        operator: () => {
          addSimpleDropdownOptions(filter, customerSupportSettings.openPhoneOperatorDropdown);
        },
        operatorPhoneNumber: () => {
          addSimpleDropdownOptions(filter, customerSupportSettings.openPhonePhoneNumbersDropdown);
        },
        customerSupportBusinessFlowAssignedTo: () => {
          for (const [key, value] of Object.entries(customerSupportSettings.openPhoneOperatorDropdown)) {
            filter.options?.push({ value: value.brightscripUsername, name: value.name });
          }
        },
      };

      const handler = filterHandlers[filter.id];
      if (handler) {
        handler();
      }
    } else if (filter.id === 'caseType') {
      const settings = await lastValueFrom(
        this.settingsStore.getSettings<SharedBusinessFlowProcessFlowPharmacyPatientEngagementPharmacyPatientProductSettingsSchema>(
          'PharmacyPatientEngagement',
          'PharmacyPatientProductFlags',
        ),
      );

      filter.options = Object.values(settings?.flags ?? {})
        .flatMap(flag => flag.combinations ?? [])
        .filter(combination => combination.enabled)
        .map(combination => ({
          name: combination.title,
          value: combination.id,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    } else if (filter.id === 'bVerificationBusinessFlowAssignedTo') {
      filter.options = [];
      const bVerificationSettings = await lastValueFrom(
        this.settingsStore
          .getSettings<SharedBusinessFlowProcessFlowBVerificationSettingsSchema>(
            'businessFlow',
            SHARED_BUSINESS_FLOW_PROCESS_FLOW_B_VERIFICATION_TYPE,
          )
          .pipe(map(settings => settings)),
      );

      if (!bVerificationSettings) {
        return filter;
      }

      for (const [key, value] of Object.entries(bVerificationSettings.assigneeDropdown)) {
        filter.options?.push({ value: key, name: value.name });
      }
    }

    return filter;
  }
}
